import { CheckboxCard } from "./CheckboxCard";
import { FormField } from "./FormField";
import { activeQuestions, dependsOnQuestion } from "./active-questions";
import { StepFooter } from "./StepFooter";
import type { StepProps } from "./step-props";
import type { InquiryProfileAnswers, PackageFormData } from "./types";
import { validateContentStep } from "./validators";
import { type InquiryPackQuestionConfig } from "./inquiry-profile-schema";
import step from "./step.module.css";
import styles from "./ContentStep.module.css";

/**
 * Text/textarea/select pack question rendered through the shared FormField
 * primitive. Pack answers live in the `profileAnswers` bag keyed by question
 * id (not on PackageFormData), so FormField gets a one-entry synthetic
 * formData — the casts are contained here and FormField only ever reads
 * `formData[field]`.
 */
function PackQuestionField({
  question,
  value,
  autofilledFields,
  showValidation,
  error,
  onChange,
}: {
  question: InquiryPackQuestionConfig;
  value: string;
  autofilledFields?: Set<string>;
  showValidation: boolean;
  error?: string;
  onChange: (value: string) => void;
}): React.JSX.Element {
  const field = question.id as keyof PackageFormData;
  const formData = { [question.id]: value } as Partial<PackageFormData>;
  if (question.type === "textarea") {
    return (
      <FormField
        as="textarea"
        label={question.label}
        field={field}
        formData={formData}
        autofilledFields={autofilledFields}
        required={question.required}
        showValidation={showValidation}
        error={error}
        hint={question.hint}
        rows={4}
        onChange={onChange}
      />
    );
  }
  if (question.type === "select") {
    return (
      <FormField
        as="select"
        label={question.label}
        field={field}
        formData={formData}
        autofilledFields={autofilledFields}
        required={question.required}
        showValidation={showValidation}
        error={error}
        hint={question.hint}
        placeholder="Select..."
        options={(question.options ?? []).map((option) => ({
          value: option,
          label: option,
        }))}
        onChange={onChange}
      />
    );
  }
  if (question.type === "integer") {
    return (
      <FormField
        label={question.label}
        field={field}
        formData={formData}
        autofilledFields={autofilledFields}
        required={question.required}
        showValidation={showValidation}
        error={error}
        hint={question.hint}
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        onChange={onChange}
      />
    );
  }
  return (
    <FormField
      label={question.label}
      field={field}
      formData={formData}
      autofilledFields={autofilledFields}
      required={question.required}
      showValidation={showValidation}
      error={error}
      hint={question.hint}
      onChange={onChange}
    />
  );
}

/**
 * How many of a Profile's questions one page of the content step shows.
 * A rendering decision and nothing else: nothing about pages is stored,
 * and the pack is one list in the Company's order (DESIGN.md, the
 * discipline pack). The pages are cut from the questions asked right
 * now, so a question whose condition is not met holds no slot.
 */
export const PACK_QUESTIONS_PER_PAGE = 4;

interface ContentStepProps extends StepProps {
  autofilledFields: Set<string>;
  hasTechRiderFile: boolean;
  /** Which page of the pack is showing; the wizard owns it and resets it
   *  to 0 with the step. Absent is page 0 of one. */
  page?: number;
  /** Continue on a page that is not the last, and Back on one that is not
   *  the first, move a page rather than a step. */
  onPageChange?: (page: number) => void;
}

function chunk<T>(items: readonly T[], size: number): T[][] {
  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    pages.push(items.slice(i, i + size));
  }
  return pages.length > 0 ? pages : [[]];
}

export function ContentStep({
  profile,
  formData,
  autofilledFields,
  hasTechRiderFile,
  showValidation,
  updateField,
  onBack,
  onContinue,
  onShowValidation,
  continueLabel,
  page = 0,
  onPageChange,
}: ContentStepProps): React.JSX.Element {
  const copy = profile.contentStep;
  const errors = validateContentStep(formData, profile, hasTechRiderFile);

  const packAnswers: InquiryProfileAnswers = formData.profileAnswers ?? {};

  // The pages are cut from the questions asked right now: a question
  // whose condition is off does not hold a slot, so answering "indoors"
  // never leaves an empty page behind.
  const visible = activeQuestions(profile, formData, hasTechRiderFile);
  const pages = chunk(visible, PACK_QUESTIONS_PER_PAGE);
  const current = Math.min(Math.max(page, 0), pages.length - 1);
  const lastPage = current === pages.length - 1;

  const idsOnPage = (index: number): string[] =>
    pages[index].map(({ id }) => id);

  // The answer goes up as it stands. A question the new answer no longer
  // asks keeps its answer in the draft and is simply not asked.
  const setPackAnswer = (
    id: string,
    value: string | boolean | undefined,
  ): void => {
    const next = { ...packAnswers };
    if (value === undefined || value === "") {
      delete next[id];
    } else {
      next[id] = value;
    }
    updateField("profileAnswers", next);
  };

  const packStringAnswer = (id: string): string => {
    const value = packAnswers[id];
    return typeof value === "string" ? value : "";
  };

  // A question asked on another's answer is set in from the edge, so it
  // reads as following from what was just answered rather than standing
  // as one of its own.
  const renderPackQuestion = (
    question: InquiryPackQuestionConfig,
  ): React.JSX.Element => {
    const field =
      question.type === "checkbox" ? (
        <CheckboxCard
          label={question.label}
          description={question.description}
          checked={packAnswers[question.id] === true}
          autofilledFor={question.id}
          autofilledFields={autofilledFields}
          onChange={(v) => setPackAnswer(question.id, v)}
        />
      ) : (
        <PackQuestionField
          question={question}
          value={packStringAnswer(question.id)}
          autofilledFields={autofilledFields}
          showValidation={showValidation}
          error={errors[question.id]}
          onChange={(v) => setPackAnswer(question.id, v)}
        />
      );
    if (!dependsOnQuestion(question)) {
      return <div key={question.id}>{field}</div>;
    }
    return (
      <div key={question.id} className={styles.followingQuestion}>
        {field}
      </div>
    );
  };

  // A page is gated on its own questions alone, so page one never blocks
  // on page three. The last page is the one that leaves the step, and it
  // answers for the whole pack: an error left on an earlier page — reached
  // by Back, or by a panel jump — sends the Customer to that page rather
  // than on to the next step with a hole in it.
  const handleContinue = (): void => {
    const here = idsOnPage(current).filter((id) => id in errors);
    if (here.length > 0) {
      onShowValidation(here);
      return;
    }
    if (!lastPage) {
      onPageChange?.(current + 1);
      return;
    }
    for (let index = 0; index < pages.length; index++) {
      const there = idsOnPage(index).filter((id) => id in errors);
      if (there.length === 0) continue;
      onPageChange?.(index);
      onShowValidation(there);
      return;
    }
    if (Object.keys(errors).length === 0) {
      onContinue();
    } else {
      onShowValidation(Object.keys(errors));
    }
  };

  const handleBack =
    current > 0 && onPageChange ? () => onPageChange(current - 1) : onBack;

  return (
    <div className={step.step}>
      <div>
        <h1 className={step.heading}>{copy.title}</h1>
        {autofilledFields.size > 0 && (
          <p className={styles.intro}>
            {copy.autofillNotice}
          </p>
        )}
        <div className={styles.fields}>
          {pages[current].map(renderPackQuestion)}

          {lastPage && (
            <FormField
              as="textarea"
              label={copy.additionalInfo.label}
              field={copy.additionalInfo.field}
              formData={formData}
              autofilledFields={autofilledFields}
              rows={10}
              onChange={(v) => updateField(copy.additionalInfo.field, v)}
            />
          )}
        </div>
      </div>

      <StepFooter
        onBack={handleBack}
        onContinue={handleContinue}
        continueLabel={lastPage ? continueLabel : "Next questions"}
      />
    </div>
  );
}
